import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Box, CircularProgress,Stack, Typography } from '@mui/material'
import Decimal from 'decimal.js'
import { useContext, useEffect, useMemo, useState } from 'react'

import { useBalances } from '@/hooks/use-balances'
import useTrans from '@/hooks/useTrans'
import { formatSafe } from '@/utils/numeric'

import { WrappedTokensFlowContext } from '../context'

const Container = ({ onNext }: { onNext: () => void }) => {
  const trans = useTrans()
  const { balances } = useBalances()
  const { selectedToken, prevTokenBalance } = useContext(WrappedTokensFlowContext)
  const [isLoadingDelay, setIsLoadingDelay] = useState(true)

  useEffect(() => {
    /**
     * Delay a bit to show CircularProgress in the case users click on the button `I have deposited USDT`
     *  after the balance is changed already
     */
    setTimeout(() => {
      setIsLoadingDelay(false)
    }, 300)
  }, [])

  const latestTokenBalance = useMemo(() => {
    if (!selectedToken) return 0
    return balances[selectedToken.symbol] || 0
  }, [balances, selectedToken])

  if (!selectedToken) return null

  const balanceIncrementAmount = new Decimal(latestTokenBalance).sub(new Decimal(prevTokenBalance))
  const isConfirmed = balanceIncrementAmount.greaterThan(0)
  const isShowSpinner = !isConfirmed || isLoadingDelay

  return (
    <Stack>
      <Typography className="text-sm leading-[28px]">
        {trans.wrapped_token.please_wait_the_transaction}&nbsp;
        <span className="underline font-semibold cursor-pointer" onClick={onNext} data-testid="btn-learn-more">
          {trans.wrapped_token.learn_more}
        </span>
      </Typography>

      <Box
        display="flex"
        alignItems="center"
        flexDirection="column"
        gap="8px"
        className="py-3 mt-3"
      >
        { /** Example output: `Your balance: 80.1235 reUSD` or `Your balance: xx.yyy (+500 reUSD)` */ }
        <Box display="flex" gap="4px" flexWrap="wrap" justifyContent="center">
          <Typography className="text-base font-semibold whitespace-nowrap" data-testid="your-balance">
            {trans.wrapped_token.your_balance}:{' '}
            {formatSafe(latestTokenBalance, selectedToken.decimals)}{' '}
            {isShowSpinner && selectedToken.symbol} { /** Your balance: xx.yyyy reUSD */ }
          </Typography>
          {!isShowSpinner && (
            <Typography className="font-semibold whitespace-nowrap" data-testid="your-balance-increment">
              (
              <span className="text-[#54B762]">
                {formatSafe(balanceIncrementAmount, selectedToken.decimals, { sign: true })}
              </span>{' '}
              {selectedToken.symbol}) { /** (+500 reUSD) */ }
            </Typography>
          )}
        </Box>
        {isShowSpinner ? (
          <CircularProgress size="48px" />
        ) : (
          <CheckCircleIcon className="w-[58px] h-[58px] text-[#54B762]" />
        )}
      </Box>
    </Stack>
  )
}

export default Container
